'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

// ✅ Stable singleton — created once at module level, never recreated on render
const supabase = createClient();

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Determine user role from Supabase user object.
 * Checks user_metadata.role first, then app_metadata.role.
 * Returns 'admin' | 'partner' (default: 'partner')
 */
function resolveRole(user: any): 'admin' | 'partner' {
  const role =
    user?.user_metadata?.role ||
    user?.app_metadata?.role ||
    'partner';
  return role === 'admin' ? 'admin' : 'partner';
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'partner'>('partner');

  useEffect(() => {
    // Get initial session — sets loading to false exactly once
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setUserRole(session?.user ? resolveRole(session.user) : 'partner');
      setLoading(false);
    });

    // Listen for auth changes — does NOT call setLoading to avoid double re-render
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setUserRole(session?.user ? resolveRole(session.user) : 'partner');
      // ✅ loading is NOT set here — it was already set to false by getSession above
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    console.log('[AUTH DIAG] signIn started');

    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
    const projectRef = supabaseUrl
      ? supabaseUrl.replace('https://', '').split('.')[0]
      : 'MISSING';
    console.log('[AUTH DIAG] Supabase project ref:', projectRef);

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('[AUTH DIAG] auth error message:', (error as any).message ?? 'none');
      console.error('[AUTH DIAG] auth error status/code:', `status=${(error as any).status ?? 'n/a'} code=${(error as any).code ?? 'n/a'}`);
    } else {
      console.log('[AUTH DIAG] whether user exists:', !!data.user);
      console.log('[AUTH DIAG] whether session exists:', !!data.session);
    }

    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
